/* @flow */
import * as React from 'react';
import { translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Card, CardHeader, CardContent, Divider, Collapse, Modal } from '@material-ui/core';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { Link } from 'react-router-dom';
import DashboardStore from 'stores/DashboardStore';
import UserStore from 'stores/UserStore';
import KenticoStore from 'stores/KenticoStore';
import Notifications from '../Dashboard/widgets/Notifications/Notifications';
import SentRequests from '../Dashboard/widgets/SentRequests/SentRequests';
import FeaturedProduct from '../Dashboard/widgets/FeaturedProduct/FeaturedProduct';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import Loading from 'components/Loading/Loading';
import { ModalLink } from './components/ModalLink';
import { showInlineManualPanel } from '../PageSections/Navigation/TopMenu/TopMenu';
import type { TranslatorProps } from 'react-i18next';
import styles from './WelcomePage.module.scss';

type ModalType = $Values<typeof ModalTypes>;
const ModalTypes = Object.freeze({
  FIND_AN_ASSET: 'find-an-asset',
  EXTEND_AN_ASSET: 'extend-an-asset',
  SCHEDULE_CALIBRATION: 'schedule-calibration',
  REPORT_FAULT: 'report-fault',
  DOWNLOAD_CAL_CERT: 'download-calibration-certificate',
  DOWNLOAD_DELIVERY_NOTE: 'download-delivery-note',
  ASSIGN_ASSET: 'assign-an-asset',
  FIND_CONTRACT: 'find-order',
  EXTEND_CONTRACT: 'extend-order',
  FIND_PROJECT: 'find-project',
  ASSIGN_PROJECT_USERS: 'assign-project-users',
  EXTEND_PROJECT: 'extend-project',
  FIND_COST_CENTER: 'find-cost-center',
  ASSIGN_COST_CENTER: 'assign-cost-center',
  ARCHIVE_COST_CENTER: 'archive-cost-center',
});

type InjectedProps = {
  dashboardStore: DashboardStore,
  userStore: UserStore,
  kenticoStore: KenticoStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore', 'userStore', 'navigationStore', 'kenticoStore')
@observer
export class WelcomePage extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  @observable expandWelcomeMessage = true;

  async componentDidMount() {
    const { dashboardStore } = this.props;
    const counts = Object.keys(dashboardStore.counts);

    if (counts.length === 0) {
      await dashboardStore.loadDashboardCounts();
    }
  }

  isModalOpenCheck(type: ModalType) {
    const { modalType } = this.props.match.params;
    const modalTypeValues = Object.values(ModalTypes);
    const modalTypeExists = modalTypeValues.filter(item => item === type).length > 0;

    return modalTypeExists && modalType === type;
  }

  renderDashboardCounts() {
    const { t, dashboardStore } = this.props;
    const { loadingCounts } = dashboardStore;

    if (loadingCounts) {
      return <Loading />;
    }

    return (
      <React.Fragment>
        {Object.keys(dashboardStore.counts).map((count, index) => {
          const { icon, label, link } = dashboardStore.getDashboardCountConfig(count);

          return link ? (
            <Link to={link} key={index} className={styles.countContainer}>
              <svg>
                <use xlinkHref={icon} />
              </svg>
              <b className={styles.countNumber}>{dashboardStore.counts[count]}</b>
              <span className={styles.countLabel}>{t(label)}</span>
            </Link>
          ) : (
            <div key={index} className={styles.countContainer}>
              <svg>
                <use xlinkHref={icon} />
              </svg>
              <b className={styles.countNumber}>{dashboardStore.counts[count]}</b>
              <span className={styles.countLabel}>{t(label)}</span>
            </div>
          );
        })}
      </React.Fragment>
    );
  }

  renderCardHeader(type: string) {
    const { t } = this.props;
    let config = {};

    switch (type) {
      case 'assets':
        config.link = '/assets';
        config.icon = '#icon-assets';
        config.text = t('general:asset_plural');
        break;
      case 'profile':
        config.link = '/profile';
        config.icon = '#icon-profile';
        config.text = t('general:profile');
        break;
      case 'contracts':
        config.link = '/orders';
        config.icon = '#icon-contracts';
        config.text = t('general:contract_plural');
        break;
      case 'projects':
        config.link = '/projects';
        config.icon = '#icon-projects';
        config.text = t('general:project_plural');
        break;
      case 'cost-centers':
        config.link = '/cost-centers';
        config.icon = '#icon-cost-centers';
        config.text = t('general:cost_center_plural');
        break;
    }

    return (
      <Link to={config.link} className={styles.cardHeader}>
        <svg>
          <use xlinkHref={config.icon} />
        </svg>
        <span>{config.text}</span>
      </Link>
    );
  }

  renderAssetLinks() {
    const { t } = this.props;

    return (
      <Card className={styles.assetLinks}>
        <CardHeader title={this.renderCardHeader('assets')} />
        <div className={styles.divider}>
          <Divider />
        </div>
        <CardContent>
          <div>
            <ModalLink
              type={'asset'}
              isOpen={() => this.isModalOpenCheck(ModalTypes.FIND_AN_ASSET)}
              text={t('general:welcome_page.asset.find')}
              tooltip={t('general:welcome_page.asset.link_tooltip')}
              options={{ url: 'assets', to: `/welcome-page/modal/${ModalTypes.FIND_AN_ASSET}` }}
            />
            <ModalLink
              type={'asset'}
              isOpen={() => this.isModalOpenCheck(ModalTypes.EXTEND_AN_ASSET)}
              text={t('general:welcome_page.asset.extend')}
              tooltip={t('general:welcome_page.asset.link_tooltip')}
              options={{ url: 'assets', to: `/welcome-page/modal/${ModalTypes.EXTEND_AN_ASSET}` }}
            />
            <ModalLink
              type={'asset'}
              isOpen={() => this.isModalOpenCheck(ModalTypes.SCHEDULE_CALIBRATION)}
              text={t('general:welcome_page.asset.schedule_cal')}
              tooltip={t('general:welcome_page.asset.link_tooltip')}
              options={{
                url: 'assets',
                to: `/welcome-page/modal/${ModalTypes.SCHEDULE_CALIBRATION}`,
              }}
            />
            <ModalLink
              type={'asset'}
              isOpen={() => this.isModalOpenCheck(ModalTypes.REPORT_FAULT)}
              text={t('general:welcome_page.asset.report')}
              tooltip={t('general:welcome_page.asset.link_tooltip')}
              options={{ url: 'assets', to: `/welcome-page/modal/${ModalTypes.REPORT_FAULT}` }}
            />
          </div>
          <div>
            <ModalLink
              type={'asset'}
              isOpen={() => this.isModalOpenCheck(ModalTypes.DOWNLOAD_CAL_CERT)}
              text={t('general:welcome_page.asset.view_cal')}
              tooltip={t('general:welcome_page.asset.link_tooltip')}
              options={{ url: 'assets', to: `/welcome-page/modal/${ModalTypes.DOWNLOAD_CAL_CERT}` }}
            />
            <ModalLink
              type={'asset'}
              isOpen={() => this.isModalOpenCheck(ModalTypes.DOWNLOAD_DELIVERY_NOTE)}
              text={t('general:welcome_page.asset.view_delivery_note')}
              tooltip={t('general:welcome_page.asset.link_tooltip')}
              options={{
                url: 'assets',
                to: `/welcome-page/modal/${ModalTypes.DOWNLOAD_DELIVERY_NOTE}`,
              }}
            />
            <ModalLink
              type={'asset'}
              isOpen={() => this.isModalOpenCheck(ModalTypes.ASSIGN_ASSET)}
              text={t('general:welcome_page.asset.assign')}
              tooltip={t('general:welcome_page.asset.link_tooltip')}
              options={{ url: 'assets', to: `/welcome-page/modal/${ModalTypes.ASSIGN_ASSET}` }}
            />
            <Link to="/assets/create">{t('general:welcome_page.asset.create')}</Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  renderProfileLinks() {
    const { t } = this.props;

    return (
      <Card>
        <CardHeader title={this.renderCardHeader('profile')} />
        <div className={styles.divider}>
          <Divider />
        </div>
        <CardContent>
          <div>
            <Link to="/profile">{t('general:welcome_page.profile.preferences')}</Link>
          </div>
          <div>
            <Link to="/support">{t('general:welcome_page.profile.support')}</Link>
          </div>
          <div>
            <Link to={{ pathname: '/notifications', state: { tab: 1 } }}>
              {t('general:welcome_page.profile.notifications')}
            </Link>
          </div>
          <div>
            <Link to={{ pathname: '/notifications', state: { tab: 0 } }}>
              {t('general:welcome_page.profile.requests')}
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  renderOrderLinks() {
    const { t } = this.props;

    return (
      <Card>
        <CardHeader title={this.renderCardHeader('contracts')} />
        <div className={styles.divider}>
          <Divider />
        </div>
        <CardContent>
          <ModalLink
            type={'contract'}
            isOpen={() => this.isModalOpenCheck(ModalTypes.FIND_CONTRACT)}
            text={t('general:welcome_page.contracts.find')}
            tooltip={t('general:welcome_page.contracts.link_tooltip')}
            options={{ url: 'orders', to: `/welcome-page/modal/${ModalTypes.FIND_CONTRACT}` }}
          />
          <ModalLink
            type={'contract'}
            isOpen={() => this.isModalOpenCheck(ModalTypes.EXTEND_CONTRACT)}
            text={t('general:welcome_page.contracts.extend')}
            tooltip={t('general:welcome_page.contracts.link_tooltip')}
            options={{ url: 'orders', to: `/welcome-page/modal/${ModalTypes.EXTEND_CONTRACT}` }}
          />
        </CardContent>
      </Card>
    );
  }

  renderProjectLinks() {
    const { t } = this.props;

    return (
      <Card>
        <CardHeader title={this.renderCardHeader('projects')} />
        <div className={styles.divider}>
          <Divider />
        </div>
        <CardContent>
          <ModalLink
            type={'project'}
            isOpen={() => this.isModalOpenCheck(ModalTypes.FIND_PROJECT)}
            text={t('general:welcome_page.projects.find')}
            tooltip={t('general:welcome_page.projects.link_tooltip')}
            options={{ url: 'projects', to: `/welcome-page/modal/${ModalTypes.FIND_PROJECT}` }}
          />
          <ModalLink
            type={'project'}
            isOpen={() => this.isModalOpenCheck(ModalTypes.ASSIGN_PROJECT_USERS)}
            text={t('general:welcome_page.projects.view_assign')}
            tooltip={t('general:welcome_page.projects.link_tooltip')}
            options={{
              url: 'projects',
              to: `/welcome-page/modal/${ModalTypes.ASSIGN_PROJECT_USERS}`,
            }}
          />
          <Link to="/projects/create">{t('general:welcome_page.projects.create')}</Link>
          <ModalLink
            type={'project'}
            isOpen={() => this.isModalOpenCheck(ModalTypes.EXTEND_PROJECT)}
            text={t('general:welcome_page.projects.extend')}
            tooltip={t('general:welcome_page.projects.link_tooltip')}
            options={{ url: 'projects', to: `/welcome-page/modal/${ModalTypes.EXTEND_PROJECT}` }}
          />
        </CardContent>
      </Card>
    );
  }

  renderCostCentreLinks() {
    const { t } = this.props;

    return (
      <Card>
        <CardHeader title={this.renderCardHeader('cost-centers')} />
        <div className={styles.divider}>
          <Divider />
        </div>
        <CardContent>
          <ModalLink
            type={'costcenter'}
            isOpen={() => this.isModalOpenCheck(ModalTypes.FIND_COST_CENTER)}
            text={t('general:welcome_page.cost-center.find')}
            tooltip={t('general:welcome_page.cost-center.link_tooltip')}
            options={{
              url: 'cost-centers',
              to: `/welcome-page/modal/${ModalTypes.FIND_COST_CENTER}`,
            }}
          />
          <ModalLink
            type={'costcenter'}
            isOpen={() => this.isModalOpenCheck(ModalTypes.ASSIGN_COST_CENTER)}
            text={t('general:welcome_page.cost-center.view_assign')}
            tooltip={t('general:welcome_page.cost-center.link_tooltip')}
            options={{
              url: 'cost-centers',
              to: `/welcome-page/modal/${ModalTypes.ASSIGN_COST_CENTER}`,
            }}
          />
          <Link to="/cost-centers/create">{t('general:welcome_page.cost-center.create')}</Link>
          <ModalLink
            type={'costcenter'}
            isOpen={() => this.isModalOpenCheck(ModalTypes.ARCHIVE_COST_CENTER)}
            text={t('general:welcome_page.cost-center.archive')}
            tooltip={t('general:welcome_page.cost-center.link_tooltip')}
            options={{
              url: 'cost-centers',
              to: `/welcome-page/modal/${ModalTypes.ARCHIVE_COST_CENTER}`,
            }}
          />
        </CardContent>
      </Card>
    );
  }

  renderHelpToolLink() {
    const { t } = this.props;

    return <a onClick={showInlineManualPanel}>{t('general:welcome_page.help_tool')}</a>;
  }

  renderWelcomeMessage() {
    const { t } = this.props;

    return (
      <Card className={styles.welcomeMessage}>
        <CardHeader
          onClick={() => (this.expandWelcomeMessage = !this.expandWelcomeMessage)}
          title={
            <div className={styles.welcomeMessageHeader}>
              <span>{t('general:welcome_page.header')}</span>
              <svg>
                <use
                  xlinkHref={this.expandWelcomeMessage ? '#icon-chevron-down' : '#icon-chevron-up'}
                />
              </svg>
            </div>
          }
        />
        <Collapse in={this.expandWelcomeMessage}>
          <Divider />
          <CardContent>
            <p dangerouslySetInnerHTML={{ __html: t('general:welcome_page.message_part_1') }} />
            <p>
              <span
                dangerouslySetInnerHTML={{ __html: t('general:welcome_page.message_part_2') }}
              />
              &nbsp;
              {this.renderHelpToolLink()}
              &nbsp;
              <span
                dangerouslySetInnerHTML={{ __html: t('general:welcome_page.message_part_3') }}
              />
              &nbsp;
              <Link to={'/support'}>{t('general:welcome_page.message_part_support')}</Link>.
            </p>
          </CardContent>
        </Collapse>
      </Card>
    );
  }

  removeGlobalMessage(event) {
    const globalMessageBannerElement = event.target.parentElement;
    globalMessageBannerElement.remove();
  }

  render() {
    const {
      kenticoStore: { globalMessageBanner },
      t,
      userStore,
    } = this.props;
    const displayGlobalMessageBanner =
      globalMessageBanner.content && !globalMessageBanner.loading
        ? globalMessageBanner.content.globalMessage.value
        : null;
    const { activeUser } = userStore;

    return (
      <PageWrapper isFlex={false}>
        <h1 className={styles.welcomePageHeader}>
          {t('general:welcome_page.welcome')} {activeUser.full_name}
        </h1>
        {displayGlobalMessageBanner && (
          <div className={styles.globalMessageBanner}>
            <button className={styles.closeButton} onClick={this.removeGlobalMessage}>
              ×
            </button>
            <p dangerouslySetInnerHTML={{ __html: displayGlobalMessageBanner }} />
          </div>
        )}
        <div className={styles.container}>
          <div className={styles.containerLeftColumn}>
            <Card className={styles.box}>{this.renderDashboardCounts()}</Card>
            {this.renderWelcomeMessage()}
            <div className={styles.links}>
              {this.renderAssetLinks()}
              {this.renderProfileLinks()}
              {this.renderOrderLinks()}
              {this.renderProjectLinks()}
              {this.renderCostCentreLinks()}
            </div>
            <Card className={styles.bannerFooter}>
              <a href="https://www.electrorent.com/uk/services/asset-optimisation" target="_blank">
                <img src={`${process.env.PUBLIC_URL}/img/welcome-page-banner.png`} />
              </a>
            </Card>
          </div>
          <div className={styles.containerRightColumn}>
            <FeaturedProduct />
            <Notifications />
            <SentRequests />
          </div>
        </div>
      </PageWrapper>
    );
  }
}
